import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import MoneyLabel from '../../../components/label/MoneyLabel';
import DateLabel from '../../../components/label/DateLabel';
import { numberToString } from '../../../components/special/numberConverter';
import Pagination from '../../../components/special/Pagination';
import ExoAlert from '../../../components/exo/ExoAlert';
import ExoDialog from '../../../components/exo/ExoDialog';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import NewTravelExpense, {
  meansOfTransportationOptions,
} from '../../new-requests/NewTravelExpense';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PostAddIcon from '@mui/icons-material/PostAdd';

export const TravelExpensePresetCard = ({
  preset,
  project,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);

  // Lazily load the travel expenses for this preset once it is expanded.
  const [expenses, setExpenses] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [usageCount, setUsageCount] = useState(preset.usageCount ?? 0);

  useEffect(() => {
    setUsageCount(preset.usageCount ?? 0);
  }, [preset]);

  function loadExpenses() {
    DBRequest({
      config,
      path: `travel-expenses?travelExpensePresetId[eq]=${preset.id}&perPage=5`,
      method: API_GET,
      onResponse: setExpenses,
      onLoading: setExpensesLoading,
    });
  }

  useEffect(() => {
    if (!open || !config || expenses) return;
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config]);

  function handleCreated() {
    setRequestOpen(false);
    setUsageCount((count) => count + 1);
    if (open) loadExpenses();
    else setExpenses(null);
  }

  const address = preset.destinationAddress;
  const destination = address
    ? [address.zipCode, address.city].filter(Boolean).join(' ') ||
      address.street
    : null;

  const route = [preset.fromLocation, preset.toLocation]
    .filter(Boolean)
    .join(' \u2192 ');
  const title = route || destination;

  const distanceLabel = `${numberToString(preset.distanceKilometers, 2)} km`;

  return (
    <Box
      sx={{
        bgcolor: colors.bgInput,
        border: `1px solid ${colors.selected}`,
      }}
      className="flex flex-col w-full rounded-lg"
    >
      <Box className="flex items-center gap-2 pr-2">
        <Button
          onClick={() => setOpen(!open)}
          className="px-2 py-2 flex items-center gap-2 min-h-[56px] flex-1"
          sx={{ justifyContent: 'space-between' }}
        >
        <Box className="flex items-center gap-2">
          <DirectionsCarIcon />
          <Typography fontWeight={500}>
            {title || distanceLabel}
            {title && (
              <span className="opacity-50 ml-2" style={{ fontSize: 10 }}>
                {distanceLabel}
              </span>
            )}
          </Typography>
        </Box>
        <Box className="flex gap-2 items-center">
          <Tooltip title={t('Times used')}>
            <Chip
              size="small"
              icon={<HistoryIcon fontSize="small" />}
              label={`${usageCount}×`}
              sx={{
                height: 22,
                bgcolor: colors.info + '20',
                color: colors.info,
                '& .MuiChip-icon': { color: colors.info },
              }}
            />
          </Tooltip>
          <Typography fontWeight={500} className="monospace">
            <MoneyLabel money={preset.totalPrice} />
          </Typography>
          <ArrowDropDownIcon
            sx={{ transform: `rotate(${open ? '180deg' : '0deg'})` }}
          />
        </Box>
        </Button>

        {/* Sibling of the expand button: a button must not nest another one. */}
        <IconButton
          size="small"
          sx={{ opacity: 0.5, ':hover': { opacity: 1 } }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(preset);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('Edit')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmDelete(true);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('Delete')}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Deliberately outside the expand button: creating a travel expense
            from the preset is the card's main action and deserves its own,
            visible button. */}
        <Button
          variant="outlined"
          size="small"
          className="whitespace-nowrap shrink-0"
          startIcon={<PostAddIcon />}
          onClick={() => setRequestOpen(true)}
        >
          <span className="hidden sm:inline">{t('New Travel Expense')}</span>
        </Button>
      </Box>
      <Collapse in={open}>
        <Box className="flex flex-col px-4 pb-3 gap-2">
          <Box className="flex gap-6 flex-wrap">
            <Stat label={t('Distance (km)')} value={distanceLabel} />
            {Boolean(preset.durationHours) && (
              <Stat
                label={t('Duration (hours)')}
                value={numberToString(preset.durationHours, 2)}
              />
            )}
            <Stat
              label={t('Price per Kilometer')}
              value={<MoneyLabel money={preset.pricePerKilometer} />}
            />
            <Stat label={t('Total')} value={<MoneyLabel money={preset.totalPrice} />} />
          </Box>
          {address && (
            <Box>
              <Typography fontSize={10} className="opacity-50">
                {t('Address')}
              </Typography>
              <Typography className="flex items-center gap-1">
                <LocationOnIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                {`${address.street || ''} ${address.houseNumber || ''}`.trim()}
                {(address.zipCode || address.city) &&
                  `, ${[address.zipCode, address.city].filter(Boolean).join(' ')}`}
              </Typography>
            </Box>
          )}

          <Box className="flex flex-col gap-1 pt-1">
            <Typography fontSize={10} className="opacity-50">
              {t('Travel Expenses')}
            </Typography>
            {!expenses ? (
              <Typography variant="body2" className="opacity-50">
                {t('Loading')}...
              </Typography>
            ) : expenses.data.length === 0 ? (
              <Typography variant="body2" className="opacity-50">
                {t('No data available')}
              </Typography>
            ) : (
              <>
                <Box
                  className="flex flex-col gap-1"
                  sx={{ opacity: expensesLoading ? 0.5 : 1 }}
                >
                  {expenses.data.map((expense) => (
                    <ExpenseRow
                      key={expense.id}
                      expense={expense}
                      colors={colors}
                      t={t}
                    />
                  ))}
                </Box>
                {expenses.meta?.lastPage > 1 && (
                  <Pagination
                    data={expenses}
                    setData={setExpenses}
                    setLoading={setExpensesLoading}
                    isSmall
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Collapse>
      <ExoAlert
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onClickAway={() => setConfirmDelete(false)}
        onSubmit={() => {
          setConfirmDelete(false);
          onDelete(preset);
        }}
        alert={{
          type: 'warning',
          message: t('Are you sure you want to delete this destination?'),
          submitLabel: t('Delete'),
          cancelLabel: t('Cancel'),
        }}
      />
      <ExoDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        limitWidth
        maxWidth="1250px"
      >
        <NewTravelExpense
          onCreate={handleCreated}
          onCancel={() => setRequestOpen(false)}
          startData={{
            project: project || preset.project,
            travelExpensePresetId: preset.id,
            fromLocation: preset.fromLocation,
            toLocation: preset.toLocation,
            distanceKilometers: preset.distanceKilometers,
            pricePerKilometer: preset.pricePerKilometer,
            durationHours: preset.durationHours,
            destinationAddress: preset.destinationAddress || null,
          }}
        />
      </ExoDialog>
    </Box>
  );
};

const Stat = ({ label, value }) => (
  <Box>
    <Typography fontSize={10} className="opacity-50">
      {label}
    </Typography>
    <Typography className="monospace">{value}</Typography>
  </Box>
);

const ExpenseRow = ({ expense, colors, t }) => {
  const meansLabel =
    meansOfTransportationOptions.find(
      (option) => option.value === expense.meansOfTransportation
    )?.label || expense.meansOfTransportation;

  const driver = expense.user
    ? `${expense.user.firstName} ${expense.user.lastName}`
    : '';

  return (
    <Box
      className="flex items-center justify-between rounded-md px-2 py-1"
      sx={{ border: `1px solid ${colors.selected}` }}
    >
      <Box className="flex flex-col">
        <Typography variant="body2">
          <DateLabel date={expense.startTime} />
        </Typography>
        <Typography fontSize={10} className="opacity-50">
          {[driver, t(meansLabel || '')].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Typography className="monospace">
        <MoneyLabel money={expense.totalPrice} />
      </Typography>
    </Box>
  );
};

export default TravelExpensePresetCard;
